import {Rect} from "../../../../../Utils/ThreeJS/VectorStructs.js";

export interface GeoBounds {
	north: number;  // top latitude
	south: number;  // bottom latitude
	east: number;   // right longitude
	west: number;   // left longitude
}

export interface TileValidation {
	widthKm: string;
	heightKm: string;
	tileSizeKm: string;
	totalTiles: number;
	zoom: number;
	tileCountX: number;
	tileCountY: number;
}

// old version (dunno if meaningfully different)
/*export function DegreesToTilePos(lat: number, lon: number, zoom: number) {
	const latRad = (lat * Math.PI) / 180.0;
	const n = Math.pow(2.0, zoom);
	const xtile = Math.floor(((lon + 180.0) / 360.0) * n);
	const ytile = Math.floor(
		((1.0 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2.0) * n
	);
	return {tileX: xtile, tileY: ytile};
}*/

/** Convert lat/lon to tile coordinates. See: https://gis.stackexchange.com/a/310061
 * @param floor_forTileFetch If true, returns floored integers (for tile fetching). If false, returns precise floating-point tile positions without flooring (useful for anchoring). */
export function DegreesToTilePos(lat: number, lon: number, zoom: number, floor_forTileFetch: boolean) {
	// Clip latitude to valid range
	const clippedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
	const clippedLon = Math.max(-180, Math.min(180, lon));

	const latRad = (clippedLat * Math.PI) / 180.0;
	const n = Math.pow(2.0, zoom);

	const xtile = ((clippedLon + 180.0) / 360.0) * n;
	const ytile = ((1.0 - Math.log(Math.tan(latRad) + 1.0 / Math.cos(latRad)) / Math.PI) / 2.0) * n;

	if (floor_forTileFetch) {
		return {tileX: Math.floor(xtile), tileY: Math.floor(ytile)};
	}
	return {tileX: xtile, tileY: ytile};
}

/** Convert tile coordinates back to lat/lon (eg. for measuring actual area).
Note: latitude is exclusively calculated from tileY, and longitude is exclusively calculated from tileX. */
export function TilePosToDegrees(tileX: number, tileY: number, zoom: number) {
	const n = Math.pow(2.0, zoom);
	const lon_deg = (tileX / n) * 360.0 - 180.0;
	const lat_rad = Math.atan(Math.sinh(Math.PI * (1 - (2 * tileY) / n)));
	const lat_deg = (lat_rad * 180.0) / Math.PI;
	return {latitude: lat_deg, longitude: lon_deg};
}

/** Get tile size in meters at given latitude (higher latitudes, ie. those farther from the equator, have smaller tile sizes) */
export function GetTileSizeMeters(latitude: number, zoom: number): number {
	const metersPerPixel = GetGroundResolution(latitude, zoom);
	return metersPerPixel * 256; // 256px per tile
}

/** Calculate ground resolution (meters per pixel) */
export function GetGroundResolution(latitude: number, zoom: number): number {
	const earthCircumference = 40075017; // meters at equator
	const latRadians = (latitude * Math.PI) / 180;
	const metersPerPixel =
		(earthCircumference * Math.cos(latRadians)) / Math.pow(2, zoom + 8);
	return metersPerPixel;
}

/** Convert longitude difference to meters at a given latitude */
export function LonDiffToMeters(lonDiff: number, latitude: number): number {
	const earthCircumference = 40075017; // meters at equator
	const latRadians = (latitude * Math.PI) / 180;
	return (lonDiff / 360) * earthCircumference * Math.cos(latRadians);
}

/** Convert latitude difference to meters */
export function LatDiffToMeters(latDiff: number): number {
	const earthCircumference = 40075017; // meters at equator
	return (latDiff / 360) * earthCircumference;
}

// Calculate distance between two lat/lon points (Haversine formula)
/*export function CalculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
	const R = 6371; // Earth's radius in kilometers
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
		Math.cos((lat2 * Math.PI) / 180) *
		Math.sin(dLon / 2) *
		Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}*/

export function ValidateTileRequest(bounds: GeoBounds, zoom: number, maxRegionKm: number, maxTileCount: number): TileValidation {
	// Convert bounds to tile coordinates
	const {tileX: westTile, tileY: northTile} = DegreesToTilePos(bounds.north, bounds.west, zoom, true);
	const {tileX: eastTile, tileY: southTile} = DegreesToTilePos(bounds.south, bounds.east, zoom, true);

	// Calculate tile coverage needed
	const tileCountX = Math.abs(eastTile - westTile) + 1;
	const tileCountY = Math.abs(southTile - northTile) + 1;
	const totalTiles = tileCountX * tileCountY;

	// Calculate actual ground coverage
	const centerLat = (bounds.north + bounds.south) / 2;
	const tileSizeMeters = GetTileSizeMeters(centerLat, zoom);
	const tileSizeKm = tileSizeMeters / 1000;

	const totalWidthKm = tileCountX * tileSizeKm;
	const totalHeightKm = tileCountY * tileSizeKm;
	const maxDimension = Math.max(totalWidthKm, totalHeightKm);

	// Validate against limits
	if (maxDimension > maxRegionKm) {
		throw new Error(
			`Region too large: ${maxDimension.toFixed(1)}km exceeds limit of ${maxRegionKm}km. ` +
			`Each tile covers ${tileSizeKm.toFixed(2)}km at zoom ${zoom}. Try zooming in.`
		);
	}

	// Backup tile count check
	if (totalTiles > maxTileCount) {
		throw new Error(
			`Too many tiles required: ${totalTiles} (max 200). ` +
			`Try reducing the region size or zoom level.`
		);
	}

	return {
		widthKm: totalWidthKm.toFixed(1),
		heightKm: totalHeightKm.toFixed(1),
		tileSizeKm: tileSizeKm.toFixed(2),
		totalTiles,
		zoom,
		tileCountX,
		tileCountY
	};
}